import { PaymentElement, useCheckout, ExpressCheckoutElement } from '@stripe/react-stripe-js/checkout';
import * as React from 'react';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Button, Loader, Logger } from '@mobalytics/shared';
import { css } from '@emotion/react';
import { useAccount } from '../../../../payment/hooks/account.hook';
import { genPaymentV2ErrorPath, genPaymentV2Path } from '../../../../../utils/formatter/path.formatter';
import { PaymentError } from '../../../payment-error.types';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';

interface Props {
  className?: string;
  productId: string;
  priceId: string;
  submitButtonLabel: string;
  paypalButton?: React.ReactNode;
}

export const StripeCheckoutForm: React.FC<Props> = (props) => {
  const { productId, priceId, submitButtonLabel, paypalButton } = props;
  const { replace } = useHistory();
  const { account } = useAccount();
  const checkoutState = useCheckout();
  const { search } = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  if (checkoutState.type === 'loading') {
    return <Loader />;
  }

  if (checkoutState.type === 'error') {
    return <div>Error: {checkoutState.error.message}</div>;
  }

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!account?.email) {
      Logger.error('[StripeCheckoutForm]:[handleSubmit]: account email is missing');
      return;
    }

    const { checkout } = checkoutState;
    setIsSubmitting(true);

    const confirmResult = await checkout.confirm({
      redirect: 'always',
      returnUrl: `${window.location.origin}${genPaymentV2Path(search, productId, priceId, 'success', undefined)}`,
    });

    setIsSubmitting(false);
    if (confirmResult.type === 'error') {
      replace(genPaymentV2ErrorPath(productId, priceId, search, PaymentError.PAYMENT_DECLINED));
    }
  };

  const handleConfirmExpressCheckout = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const { checkout } = checkoutState;
    setIsSubmitting(true);
    if (checkoutState.type === 'success') {
      const confirmResult = await checkout.confirm({
        expressCheckoutConfirmEvent: event,
        redirect: 'always',
        returnUrl: `${window.location.origin}${genPaymentV2Path(search, productId, priceId, 'success', undefined)}`,
      });
      setIsSubmitting(false);
      if (confirmResult.type === 'error') {
        replace(genPaymentV2ErrorPath(productId, priceId, search, PaymentError.PAYMENT_DECLINED));
      }
    }
  };

  return (
    <>
      <ExpressCheckoutElement
        options={{
          buttonType: { applePay: 'order' },
          buttonHeight: 40,
          layout: {
            maxColumns: 2,
            maxRows: 0,
            overflow: 'never',
          },
          buttonTheme: {},
          paymentMethodOrder: [],
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
          },
        }}
        onConfirm={handleConfirmExpressCheckout}
      />
      {paypalButton}
      <form onSubmit={handleSubmit} css={formCss}>
        <PaymentElement
          id="payment-element"
          options={{
            layout: 'accordion',
          }}
          onReady={() => {
            setIsReady(true);
          }}
        />
        {isReady && (
          <Button
            disabled={isSubmitting}
            id="submit"
            analyticsButtonName="Stripe Checkout Form Submit"
            loading={isSubmitting}
            css={buttonCss}
          >
            {submitButtonLabel}
          </Button>
        )}
      </form>
    </>
  );
};

const buttonCss = css`
  width: 100%;
  margin-top: 24px;
`;

const formCss = css`
  margin-top: 8px;
`;
